import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import {
  IAddServiceRequest,
  IServiceRequestCode,
  IServiceRequestDetailed,
  IServiceRequestDropdown,
} from '../interfaces/ServiceRequest/IServiceRequest';

@Injectable({ providedIn: 'root' })
export class ServiceRequestService {
  constructor(
    private readonly http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
  ) {}

  getServiceRequests(
    patientId: string | number,
    visitId?: string | number,
  ): Observable<IGenericServiceResponse<IServiceRequestDetailed[]>> {
    const headers = this.createHeaders();
    const numericPatientId = Number(patientId);
    const numericVisitId = this.toNumber(visitId);

    const url =
      numericVisitId !== undefined
        ? `${environment.BACKEND_URL}ServiceRequest/${numericPatientId}?visitId=${numericVisitId}`
        : `${environment.BACKEND_URL}ServiceRequest/${numericPatientId}`;

    return this.http.get<IGenericServiceResponse<IServiceRequestDetailed[]>>(
      url,
      { headers },
    );
  }

  addServiceRequest(
    request: IAddServiceRequest,
  ): Observable<IGenericServiceResponse<number>> {
    const payload: IAddServiceRequest = {
      ...request,
      patientId: this.toNumber(this.patientService.patientIDsignal()),
      visitId: this.toNumber(this.eocService.currentVisitId()),
    };

    return this.http.post<IGenericServiceResponse<number>>(
      `${environment.BACKEND_URL}ServiceRequest`,
      payload,
      { headers: this.createHeaders() },
    );
  }

  updateServiceRequest(
    serviceRequestId: string | number,
    request: IAddServiceRequest,
  ): Observable<IServiceResponse> {
    return this.http.put<IServiceResponse>(
      `${environment.BACKEND_URL}ServiceRequest/${Number(serviceRequestId)}`,
      request,
      { headers: this.createHeaders() },
    );
  }

  deleteServiceRequest(
    serviceRequestId: string | number,
  ): Observable<IServiceResponse> {
    return this.http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}ServiceRequest/${Number(serviceRequestId)}`,
      {
        headers: this.createHeaders(),
      },
    );
  }

  getDropdownData(): Observable<IGenericServiceResponse<IServiceRequestDropdown>> {
    return this.http.get<IGenericServiceResponse<IServiceRequestDropdown>>(
      `${environment.BACKEND_URL}ServiceRequest/DropdownData`,
      {
        headers: this.createHeaders(),
      },
    );
  }

  getCategoryDropdownData(
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    return this.getLookupDropdownData(
      'Category',
      searchTerm,
      pageNumber,
      pageSize,
    );
  }

  getProcedureCodeDropdownData(
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    return this.getLookupDropdownData(
      'ProcedureCode',
      searchTerm,
      pageNumber,
      pageSize,
    );
  }

  getProcedureReasonDropdownData(
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    return this.getLookupDropdownData(
      'ProcedureReason',
      searchTerm,
      pageNumber,
      pageSize,
    );
  }

  getMedicationAsNeededReasonDropdownData(
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    return this.getLookupDropdownData(
      'MedicationAsNeededReason',
      searchTerm,
      pageNumber,
      pageSize,
    );
  }

  getLocationRoleTypeDropdownData(
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    return this.getLookupDropdownData(
      'LocationRoleType',
      searchTerm,
      pageNumber,
      pageSize,
    );
  }

  private getLookupDropdownData(
    path: string,
    searchTerm?: string,
    pageNumber = 1,
    pageSize = 100,
  ): Observable<IGenericServiceResponse<IServiceRequestCode[]>> {
    const params = new URLSearchParams();

    if (searchTerm) {
      params.set('searchTerm', searchTerm);
    }

    params.set('pageNumber', String(pageNumber));
    params.set('pageSize', String(pageSize));

    return this.http.get<IGenericServiceResponse<IServiceRequestCode[]>>(
      `${environment.BACKEND_URL}ServiceRequest/DropdownData/${path}?${params.toString()}`,
      {
        headers: this.createHeaders(),
      },
    );
  }

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private toNumber(
    value: string | number | null | undefined,
  ): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
}
